import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { BreadcrumbItem } from '../../../../domain/src/lib/breadcrumb.model';
import { BreadcrumbComponent } from 'nebula-ui-kit';
import { BreadcrumbSyncService } from '../../../../data-access/src/lib/breadcrumb-sync.service';
import { CUSTOM_ICONS } from '../../../../const/src/lib/bread-crumb.constants';
import {
  BreadcrumbStepService,
  TranslationService,
  MenuIconService,
} from '@nebula-shared/data-access';
import { SidebarService } from '../../../../data-access/src/lib/sidebar.service';

@Component({
  selector: 'app-breadcrumb-wrapper',
  templateUrl: './breadcrumb-wrapper.component.html',
  styleUrls: ['./breadcrumb-wrapper.component.scss'],
  standalone: true,
  imports: [BreadcrumbComponent],
})
export class BreadcrumbWrapperComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly breadcrumbSync = inject(BreadcrumbSyncService);
  private readonly breadcrumbStep = inject(BreadcrumbStepService);
  private readonly translation = inject(TranslationService);
  private readonly sidebarService = inject(SidebarService);
  private readonly menuIconService = inject(MenuIconService);

  /** Tramos tal como los arma quien los publica (por ruta). */
  private readonly itemsFromRoute = signal<BreadcrumbItem[]>([]);

  /**
   * Mapeo de módulos a sus iconos (applicationFavicon)
   */
  private readonly MODULE_ICONS: Record<string, string> = {
    Contabilidad: 'Receipt',
    Tesorería: 'Landmark',
    Presupuesto: 'LibraryBig',
  };

  /**
   * Mapeo de modos a sus claves de traducción
   */
  private readonly MODE_TRANSLATION_KEYS: Record<string, string> = {
    Listado: 'ACTIONS.LIST',
    Crear: 'ACTIONS.CREATE',
    Editar: 'ACTIONS.EDIT',
    Consultar: 'ACTIONS.CONSULT',
    Ver: 'ACTIONS.VIEW',
  };

  /**
   * Mapeo de modos a sus iconos
   */
  private readonly MODE_ICONS: Record<string, string> = {
    Listado: 'Server',
    Crear: 'Plus',
    Editar: 'Pen',
    Consultar: 'Eye',
    Ver: 'Eye',
  };

  /**
   * Tramos que se pintan: los de la ruta, con el ultimo renombrado al paso que
   * declare la vista activa (listado, agregar, o uno propio del flujo).
   */
  readonly breadcrumbItems = computed<BreadcrumbItem[]>(() => {
    this.translation.lang();
    this.menuIconService.getIcon();
    let items = this.conGrupo(this.itemsFromRoute());
    items = this.conPaso(items);
    items = this.setPenultimateItemIcon(items);
    items = this.setModuleIcon(items);
    items = this.setModeIcon(items);

    return items;
  });

  /**
   * Agrega un icono a CUSTOM_ICONS si no existe
   */
  private ensureIconInCustomIcons(iconName: string): void {
    if (!iconName) return;

    if (!CUSTOM_ICONS.includes(iconName)) {
      CUSTOM_ICONS.push(iconName);
      this.SAFE_ICONS = CUSTOM_ICONS;
    }
  }

  /**
   * Obtiene el icono actual del menú
   */
  private getCurrentMenuIcon(): string {
    const icon = this.menuIconService.getIcon()();
    if (icon) {
      this.ensureIconInCustomIcons(icon);
      return icon;
    }
    this.ensureIconInCustomIcons('Server');
    return 'Server';
  }

  /**
   * Traduce una etiqueta de modo usando el TranslationService
   */
  private translateModeLabel(label: string): string {
    const key = this.MODE_TRANSLATION_KEYS[label];
    if (key) {
      const translated = this.translation.translate(key);
      if (translated !== key) {
        return translated;
      }
    }
    return label;
  }

  /**
   * Obtiene el modo basado en la URL
   */
  private getModeFromUrl(url: string): { mode: string; icon: string } {
    if (url.includes('/create') || url.includes('/crear')) {
      return { mode: 'Crear', icon: 'Plus' };
    } else if (url.includes('/edit') || url.includes('/editar')) {
      return { mode: 'Editar', icon: 'Pen' };
    } else if (
      url.includes('/view') ||
      url.includes('/ver') ||
      url.includes('/detail') ||
      url.includes('/consultar')
    ) {
      return { mode: 'Consultar', icon: 'Eye' };
    } else {
      return { mode: 'Listado', icon: this.getCurrentMenuIcon() };
    }
  }

  /**
   * Cambia el icono del módulo (primer elemento) al icono correcto
   */
  private setModuleIcon(items: BreadcrumbItem[]): BreadcrumbItem[] {
    if (!items || items.length === 0) return items;

    const result = [...items];
    const firstItem = result[0];

    const moduleIcon = this.MODULE_ICONS[firstItem.label];

    if (moduleIcon) {
      this.ensureIconInCustomIcons(moduleIcon);
      result[0] = { ...firstItem, icon: this._getSafeIcon(moduleIcon) };
    }

    return result;
  }

  /**
   * Cambia el icono del penúltimo elemento (el que está antes del modo)
   * Este es el elemento que corresponde a la opción del menú seleccionada
   */
  private setPenultimateItemIcon(items: BreadcrumbItem[]): BreadcrumbItem[] {
    if (!items || items.length < 2) return items;

    const result = [...items];
    const lastIndex = result.length - 1;
    const lastItem = result[lastIndex];

    const modeKeys = Object.keys(this.MODE_TRANSLATION_KEYS);
    const isMode = modeKeys.some(
      (key) => lastItem.label === key || lastItem.label === this.translateModeLabel(key),
    );

    if (isMode) {
      const menuIcon = this.getCurrentMenuIcon();
      const penultimateIndex = result.length - 2;
      const penultimateItem = result[penultimateIndex];
      if (penultimateItem.icon !== menuIcon) {
        result[penultimateIndex] = { ...penultimateItem, icon: this._getSafeIcon(menuIcon) };
      }
    }

    return result;
  }

  /**
   * Cambia el icono y traduce el último elemento según el modo
   */
  private setModeIcon(items: BreadcrumbItem[]): BreadcrumbItem[] {
    if (!items || items.length === 0) return items;

    const result = [...items];
    const lastIndex = result.length - 1;
    const lastItem = result[lastIndex];

    const modeKeys = Object.keys(this.MODE_TRANSLATION_KEYS);
    const isMode = modeKeys.some(
      (key) => lastItem.label === key || lastItem.label === this.translateModeLabel(key),
    );

    if (isMode) {
      const translatedLabel = this.translateModeLabel(lastItem.label);
      if (translatedLabel !== lastItem.label) {
        result[lastIndex] = { ...lastItem, label: translatedLabel };
      }
      return result;
    }

    if (items.length === 1) {
      return result;
    }

    const { mode, icon } = this.getModeFromUrl(this.router.url);

    this.ensureIconInCustomIcons(icon);
    const translatedLabel = this.translateModeLabel(mode);

    result[lastIndex] = {
      ...lastItem,
      icon: this._getSafeIcon(icon),
      label: translatedLabel,
    };

    return result;
  }

  /**
   * Inserta el grupo de menu de la opcion, que la ruta no nombra.
   */
  private conGrupo(items: BreadcrumbItem[]): BreadcrumbItem[] {
    if (items.length < 2) return items;

    const key = this.sidebarService.groupKeyForRoute(this.router.url);
    if (!key) return items;

    const label = this.translation.translate(key);
    if (label === key || items[0].label === label) return items;

    const groupIcon = this.sidebarService.groupIconForRoute(this.router.url) || 'Folder';
    this.ensureIconInCustomIcons(groupIcon);

    return [items[0], { label, icon: this._getSafeIcon(groupIcon) }, ...items.slice(1)];
  }

  /** Renombra el ultimo tramo con el paso que declare la vista activa. */
  private conPaso(items: BreadcrumbItem[]): BreadcrumbItem[] {
    if (!items.length) return items;

    const key = this.breadcrumbStep.keyFor(this.router.url);
    if (!key) return items;

    const label = this.translation.translate(key);
    if (label === key) return items;

    const ultimo = items.length - 1;
    return items.map((item, i) => (i === ultimo ? { ...item, label } : item));
  }

  private routerSubscription?: Subscription;
  private breadcrumbSubscription?: Subscription;
  private _allowRouterFallback = false;
  private SAFE_ICONS = CUSTOM_ICONS;

  ngOnInit(): void {
    this.breadcrumbSubscription = this.breadcrumbSync.breadcrumb$.subscribe((items) => {
      this.itemsFromRoute.set(items);
    });

    if (this._allowRouterFallback) {
      this.routerSubscription = this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => {
          if (this.breadcrumbSync.getCurrent().length === 0) {
            this._tryRouterFallback();
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.breadcrumbSubscription?.unsubscribe();
  }

  enableRouterFallback(): void {
    this._allowRouterFallback = true;
  }

  private _tryRouterFallback(): void {
    const items = this._buildMinimalBreadcrumbsFromRouter();
    if (items.length > 0) {
      this.itemsFromRoute.set(items);
    }
  }

  private _buildMinimalBreadcrumbsFromRouter(): BreadcrumbItem[] {
    const breadcrumbs: BreadcrumbItem[] = [];
    const url = this.router.url;

    if (url.includes('/login') || url.includes('/products-selection')) {
      return [];
    }

    const parts = url.split('/').filter((p) => p);
    if (parts.length < 3 || parts[0] !== 'layout') {
      return [];
    }

    const module = parts[1];
    const moduleLabel =
      module === 'accounting' ? 'Contabilidad' : module === 'budget' ? 'Presupuesto' : 'Tesorería';

    breadcrumbs.push({
      label: moduleLabel,
      url: `/layout/${module}`,
      icon: this._getSafeIcon('Folder'),
    });

    if (parts.length > 3) {
      const lastSegment = parts[parts.length - 1];
      if (lastSegment !== 'list' && lastSegment !== module) {
        breadcrumbs.push({
          label: this.formatRouteLabel(lastSegment),
          url: url,
          icon: this._getSafeIcon('File'),
        });
      }
    }

    return breadcrumbs;
  }

  private _getSafeIcon(providedIcon?: string, fallback = 'Folder'): string {
    if (providedIcon && this.SAFE_ICONS.includes(providedIcon)) {
      return providedIcon;
    }
    return fallback;
  }

  onBreadcrumbNavigate(item: BreadcrumbItem): void {
    if (item.url) {
      this.router.navigateByUrl(item.url);
    }
  }

  private formatRouteLabel(routePath: string): string {
    return routePath
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  refreshFromRouter(): void {
    const items = this._buildMinimalBreadcrumbsFromRouter();
    this.itemsFromRoute.set(items);
  }
}
